import { num, formatEuro } from "./DossierDashboard/formatters";

// Km en valeur pleine (« 60 000 ») : formatKm de formatters.js compacte en
// « 60k », trop elliptique pour la chip de synthèse.
const formatInt = (v) => new Intl.NumberFormat("fr-FR").format(v);

const rangeLabel = (values, format) => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? format(min) : format(min) + "–" + format(max);
};

// Fonction pure : lignes d'un dossier -> données de synthèse pour la carte.
// Tolère les champs absents (vehicle, dur, km, loyers, marges) : chaque
// libellé vaut null quand aucune donnée n'est disponible.
export const buildDossierSummary = (quotations) => {
  const qs = quotations || [];

  let vehicleCount = 0;
  const models = new Set();
  const durs = [];
  const kms = [];
  const rents = [];
  let weightedMarginSum = 0;
  let weightSum = 0;
  const fallbackRates = [];

  qs.forEach((q) => {
    const qty = num(q.quantity) || 1;
    vehicleCount += qty;

    const brand = q.vehicle?.brand?.name;
    const model = q.vehicle?.brandModel?.name;
    if (brand || model) {
      models.add([brand, model].filter(Boolean).join(" "));
    }

    if (num(q.dur) > 0) durs.push(num(q.dur));
    if (num(q.km) > 0) kms.push(num(q.km));
    if (num(q.totalRentHT) > 0) rents.push(num(q.totalRentHT));

    // Même esprit que computeDossierStats : taux pondéré par l'investissement.
    // L'investissement est lu comme dans useDossierStats (q.investAmtHT) avec
    // repli sur le payload brut (vehicle.investAmtHT) ; les lignes sans
    // investissement alimentent une moyenne simple de secours.
    const rate = num(q.marginTTCRate);
    const invest = num(q.investAmtHT) || num(q.vehicle?.investAmtHT);
    if (invest > 0) {
      weightedMarginSum += rate * invest * qty;
      weightSum += invest * qty;
    } else if (rate > 0) {
      fallbackRates.push(rate);
    }
  });

  let marginRate = null;
  if (weightSum > 0) {
    marginRate = weightedMarginSum / weightSum;
  } else if (fallbackRates.length > 0) {
    marginRate =
      fallbackRates.reduce((a, b) => a + b, 0) / fallbackRates.length;
  }

  const modelCount = models.size;
  const vehiclesLabel =
    vehicleCount > 0
      ? vehicleCount +
        " véhicule" +
        (vehicleCount > 1 ? "s" : "") +
        (modelCount === 1
          ? " · " + [...models][0]
          : modelCount > 1
          ? " · " + modelCount + " modèles"
          : "")
      : null;

  const durKmLabel =
    durs.length || kms.length
      ? [
          durs.length ? rangeLabel(durs, String) + " mois" : null,
          kms.length ? rangeLabel(kms, formatInt) + " km" : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;

  let rentLabel = null;
  if (rents.length) {
    const min = Math.min(...rents);
    const max = Math.max(...rents);
    rentLabel =
      (min === max ? "loyer " : "loyers ") +
      rangeLabel(rents, formatEuro) +
      " HT/mois";
  }

  return { vehicleCount, modelCount, vehiclesLabel, durKmLabel, rentLabel, marginRate };
};
